/**
 * useExchangeMagicLink Hook
 *
 * Exchanges a magic link token for auth tokens using URQL mutation.
 * Used for legacy magic link flow.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const ExchangeMagicLinkDocument = `
  mutation ExchangeMagicLink($token: String!) {
    exchangeMagicLink(token: $token) {
      account {
        id
        telegramId
        firstName
        lastName
        username
        email
      }
      tokens {
        accessToken
        refreshToken
        expiresAt
      }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface AuthAccount {
  id: string;
  telegramId: string | null;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

interface ExchangeLinkResponse {
  account: AuthAccount;
  tokens: AuthTokens;
}

interface UseExchangeMagicLinkResult {
  exchangeMagicLink: (token: string) => Promise<ExchangeLinkResponse>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Exchange a magic link token for auth tokens.
 */
export function useExchangeMagicLink(): UseExchangeMagicLinkResult {
  const [result, executeMutation] = useMutation<
    { exchangeMagicLink: ExchangeLinkResponse },
    { token: string }
  >(ExchangeMagicLinkDocument);

  const exchangeMagicLink = useCallback(
    async (token: string): Promise<ExchangeLinkResponse> => {
      try {
        const response = await executeMutation({ token });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to exchange magic link"
          );
        }

        if (!response.data?.exchangeMagicLink) {
          throw new Error("No response from server");
        }

        errorTracking.addBreadcrumb({
          category: "auth",
          message: "Magic link exchanged",
        });

        return response.data.exchangeMagicLink;
      } catch (err) {
        const exchangeError =
          err instanceof Error ? err : new Error("Failed to exchange magic link");

        errorTracking.captureError(exchangeError, {
          tags: { feature: "auth", action: "exchangeMagicLink" },
        });

        throw exchangeError;
      }
    },
    [executeMutation]
  );

  return {
    exchangeMagicLink,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
